import React from 'react';
import { GlobeIcon, ZapIcon, RefreshCwIcon, MapPinIcon, ShieldIcon, LeafIcon, ArrowRightIcon, CheckCircleIcon } from 'lucide-react';
import { trackAddToCart } from '../utils/metaPixel';
import { getHotmartUrl } from '../utils/utmParams';
export function HowItWorks() {
  return <div data-id="element-226">
      {/* ═══════════════════════════════════════════ */}
      {/* BLOCK 1 — EYEBROW + HEADLINE               */}
      {/* ═══════════════════════════════════════════ */}
      <section className="bg-cream py-16 md:py-24 px-5" data-id="element-227">
        <div className="max-w-[1100px] mx-auto text-center" data-id="element-228">
          <p className="font-sans text-sm font-semibold uppercase tracking-widest text-forest mb-4" data-id="element-229">
            The Science Behind The System
          </p>
          <h2 className="font-serif text-forest text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6 max-w-3xl mx-auto" data-id="element-230">
            Why Your Garden Keeps Dying — And Why It's Not Your Fault
          </h2>
          <p className="font-sans text-gray-500 text-base md:text-lg leading-relaxed max-w-xl mx-auto" data-id="element-231">
            Most gardeners follow generic advice from the internet. The problem?
            That advice wasn't written for YOUR zip code, YOUR climate zone, or
            YOUR season. Here's the real reason plants die — and the system that
            fixes it.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* BLOCK 2 — THE PROBLEM EXPLAINED             */}
      {/* ═══════════════════════════════════════════ */}
      <section className="bg-white py-16 md:py-24 px-5" data-id="element-232">
        <div className="max-w-[1100px] mx-auto" data-id="element-233">
          <h3 className="font-serif text-forest text-2xl md:text-3xl font-bold mb-12 text-center md:text-left" data-id="element-234">
            The Real Reason Plants Die{' '}
            <span className="text-gray-400 font-normal" data-id="element-235">
              (It's Not What You Think)
            </span>
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-236">
            {/* Column 1 */}
            <div className="text-center md:text-left" data-id="element-237">
              <div className="w-14 h-14 rounded-2xl bg-forest/10 flex items-center justify-center mx-auto md:mx-0 mb-5" data-id="element-238">
                <GlobeIcon className="w-7 h-7 text-forest" strokeWidth={1.5} data-id="element-239" />
              </div>
              <h4 className="font-sans font-bold text-forest-dark text-lg mb-3" data-id="element-240">
                Generic Advice Ignores Your Zone
              </h4>
              <p className="font-sans text-gray-500 leading-relaxed text-[15px]" data-id="element-241">
                A watering tip written for Florida will kill a garden in
                Minnesota. The USA has 13 distinct climate zones — and most
                gardening advice pretends they don't exist.
              </p>
            </div>

            {/* Column 2 — KEY PROBLEM (orange accent) */}
            <div className="text-center md:text-left" data-id="element-242">
              <div className="w-14 h-14 rounded-2xl bg-burnt/10 flex items-center justify-center mx-auto md:mx-0 mb-5" data-id="element-243">
                <ZapIcon className="w-7 h-7 text-burnt" strokeWidth={1.5} data-id="element-244" />
              </div>
              <h4 className="font-sans font-bold text-burnt text-lg mb-3" data-id="element-245">
                Extreme Weather Hits Faster Than You React
              </h4>
              <p className="font-sans text-gray-500 leading-relaxed text-[15px]" data-id="element-246">
                Frost doesn't schedule appointments. A heat wave doesn't send
                reminders. By the time you Google "how to save my plants," it's
                already too late. The damage happens in the first 6 hours.
              </p>
            </div>

            {/* Column 3 */}
            <div className="text-center md:text-left" data-id="element-247">
              <div className="w-14 h-14 rounded-2xl bg-forest/10 flex items-center justify-center mx-auto md:mx-0 mb-5" data-id="element-248">
                <RefreshCwIcon className="w-7 h-7 text-forest" strokeWidth={1.5} data-id="element-249" />
              </div>
              <h4 className="font-sans font-bold text-forest-dark text-lg mb-3" data-id="element-250">
                You Repeat the Same Cycle Every Year
              </h4>
              <p className="font-sans text-gray-500 leading-relaxed text-[15px]" data-id="element-251">
                Plant in spring. Hope for the best. Lose plants in summer heat
                or fall frost. Replace in spring. Spend more money. Without a
                system, this cycle never breaks.
              </p>
            </div>
          </div>

          {/* Divider with centered text */}
          <div className="border-t border-gray-200 pt-6" data-id="element-252">
            <p className="text-center font-serif italic text-burnt text-base md:text-lg max-w-xl mx-auto" data-id="element-253">
              "The problem isn't your green thumb — it's the missing system
              between YOU and your climate."
            </p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* BLOCK 3 — THE MECHANISM REVEAL              */}
      {/* ═══════════════════════════════════════════ */}
      <section className="py-16 md:py-24 px-5" data-id="element-254">
        <div className="max-w-[1100px] mx-auto" data-id="element-255">
          <div className="bg-[#D8F3DC] rounded-2xl md:rounded-3xl py-14 md:py-20 px-6 md:px-12" data-id="element-256">
            <div className="text-center mb-14" data-id="element-257">
              <p className="font-sans text-sm font-semibold uppercase tracking-widest text-burnt mb-4" data-id="element-258">
                Introducing The Mechanism
              </p>
              <h3 className="font-serif text-forest text-3xl md:text-4xl font-bold mb-4" data-id="element-259">
                The Hardiness Shield System™
              </h3>
              <p className="font-sans text-gray-600 max-w-2xl mx-auto leading-relaxed" data-id="element-260">
                A 3-layer protection protocol built around the official USDA
                Plant Hardiness Zone Map — the same system professional
                landscapers use, simplified into a single actionable guide.
              </p>
            </div>

            {/* Step-by-step flow */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4 items-start relative" data-id="element-261">
              {/* Step 1 */}
              <div className="bg-white rounded-xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.08)] text-center relative z-10" data-id="element-262">
                <div className="w-12 h-12 rounded-full bg-forest flex items-center justify-center mx-auto mb-4" data-id="element-263">
                  <MapPinIcon className="w-6 h-6 text-white" data-id="element-264" />
                </div>
                <p className="font-sans text-xs font-bold uppercase tracking-widest text-burnt mb-2" data-id="element-265">
                  Step 1
                </p>
                <h4 className="font-sans font-bold text-forest-dark text-lg mb-2" data-id="element-266">
                  Identify Your Zone
                </h4>
                <p className="font-sans text-gray-500 text-sm leading-relaxed" data-id="element-267">
                  Find your exact USDA Hardiness Zone using our included map.
                  Your zone determines your frost dates, heat index, and plant
                  survival thresholds.
                </p>
              </div>

              {/* Arrow 1 (hidden on mobile) */}
              <div className="hidden md:flex absolute left-[33%] top-1/2 -translate-y-1/2 -translate-x-1/2 z-20" data-id="element-268">
                <div className="w-8 h-8 rounded-full bg-forest/20 flex items-center justify-center" data-id="element-269">
                  <ArrowRightIcon className="w-4 h-4 text-forest" data-id="element-270" />
                </div>
              </div>
              {/* Arrow mobile */}
              <div className="flex md:hidden justify-center -my-2" data-id="element-271">
                <div className="w-8 h-8 rounded-full bg-forest/20 flex items-center justify-center rotate-90" data-id="element-272">
                  <ArrowRightIcon className="w-4 h-4 text-forest" data-id="element-273" />
                </div>
              </div>

              {/* Step 2 */}
              <div className="bg-white rounded-xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.08)] text-center relative z-10" data-id="element-274">
                <div className="w-12 h-12 rounded-full bg-burnt flex items-center justify-center mx-auto mb-4" data-id="element-275">
                  <ShieldIcon className="w-6 h-6 text-white" data-id="element-276" />
                </div>
                <p className="font-sans text-xs font-bold uppercase tracking-widest text-burnt mb-2" data-id="element-277">
                  Step 2
                </p>
                <h4 className="font-sans font-bold text-forest-dark text-lg mb-2" data-id="element-278">
                  Activate The Seasonal Protocol
                </h4>
                <p className="font-sans text-gray-500 text-sm leading-relaxed" data-id="element-279">
                  Each season has a specific protection checklist. You follow
                  the right protocol for YOUR zone at the RIGHT time — before
                  the weather hits, not after.
                </p>
              </div>

              {/* Arrow 2 (hidden on mobile) */}
              <div className="hidden md:flex absolute left-[67%] top-1/2 -translate-y-1/2 -translate-x-1/2 z-20" data-id="element-280">
                <div className="w-8 h-8 rounded-full bg-forest/20 flex items-center justify-center" data-id="element-281">
                  <ArrowRightIcon className="w-4 h-4 text-forest" data-id="element-282" />
                </div>
              </div>
              {/* Arrow mobile */}
              <div className="flex md:hidden justify-center -my-2" data-id="element-283">
                <div className="w-8 h-8 rounded-full bg-forest/20 flex items-center justify-center rotate-90" data-id="element-284">
                  <ArrowRightIcon className="w-4 h-4 text-forest" data-id="element-285" />
                </div>
              </div>

              {/* Step 3 */}
              <div className="bg-white rounded-xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.08)] text-center relative z-10" data-id="element-286">
                <div className="w-12 h-12 rounded-full bg-forest flex items-center justify-center mx-auto mb-4" data-id="element-287">
                  <LeafIcon className="w-6 h-6 text-white" data-id="element-288" />
                </div>
                <p className="font-sans text-xs font-bold uppercase tracking-widest text-burnt mb-2" data-id="element-289">
                  Step 3
                </p>
                <h4 className="font-sans font-bold text-forest-dark text-lg mb-2" data-id="element-290">
                  Your Garden Survives & Thrives
                </h4>
                <p className="font-sans text-gray-500 text-sm leading-relaxed" data-id="element-291">
                  With the right preparation in place, your plants are protected
                  from frost, heat waves, storms and flooding — season after
                  season, year after year.
                </p>
              </div>
            </div>

            {/* Badge */}
            <div className="text-center mt-10" data-id="element-292">
              <span className="inline-flex items-center gap-2 bg-forest text-white font-sans text-sm font-semibold px-5 py-2.5 rounded-full shadow-md" data-id="element-293">
                <CheckCircleIcon className="w-4 h-4" data-id="element-294" />
                Used by 11,000+ American homeowners
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* BLOCK 4 — FOUR SEASONS BREAKDOWN            */}
      {/* ═══════════════════════════════════════════ */}
      <section className="bg-cream py-16 md:py-24 px-5" data-id="element-295">
        <div className="max-w-[1100px] mx-auto" data-id="element-296">
          <h3 className="font-serif text-forest text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-3" data-id="element-297">
            One System. Four Seasons. Zero Dead Plants.
          </h3>
          <p className="font-sans text-gray-500 text-center mb-12 max-w-xl mx-auto" data-id="element-298">
            Each season has a dedicated protection protocol inside The 365
            Garden Shield:
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-299">
            {/* Winter */}
            <div className="bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] overflow-hidden" data-id="element-300">
              <div className="h-1.5 bg-[#AED9E0]" data-id="element-301" />
              <div className="p-6" data-id="element-302">
                <span className="text-4xl block mb-3" data-id="element-303">❄️</span>
                <h4 className="font-sans font-bold text-forest-dark text-sm uppercase tracking-wider mb-4" data-id="element-304">
                  Winter Protocol
                </h4>
                <ul className="space-y-2.5" data-id="element-305">
                  <li className="flex items-start gap-2 text-sm text-gray-600 font-sans" data-id="element-306">
                    <span className="text-[#AED9E0] mt-0.5" data-id="element-307">•</span>
                    Mulching roots before the deep freeze
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600 font-sans" data-id="element-308">
                    <span className="text-[#AED9E0] mt-0.5" data-id="element-309">•</span>
                    Identifying cold-hardy vs. vulnerable plants
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600 font-sans" data-id="element-310">
                    <span className="text-[#AED9E0] mt-0.5" data-id="element-311">•</span>
                    Emergency steps when temps drop below 20°F
                  </li>
                </ul>
              </div>
            </div>

            {/* Spring */}
            <div className="bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] overflow-hidden" data-id="element-312">
              <div className="h-1.5 bg-[#FFB7C5]" data-id="element-313" />
              <div className="p-6" data-id="element-314">
                <span className="text-4xl block mb-3" data-id="element-315">🌸</span>
                <h4 className="font-sans font-bold text-forest-dark text-sm uppercase tracking-wider mb-4" data-id="element-316">
                  Spring Protocol
                </h4>
                <ul className="space-y-2.5" data-id="element-317">
                  <li className="flex items-start gap-2 text-sm text-gray-600 font-sans" data-id="element-318">
                    <span className="text-[#FFB7C5] mt-0.5" data-id="element-319">•</span>
                    Safe transplanting windows by zone
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600 font-sans" data-id="element-320">
                    <span className="text-[#FFB7C5] mt-0.5" data-id="element-321">•</span>
                    Protecting new growth from late frosts
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600 font-sans" data-id="element-322">
                    <span className="text-[#FFB7C5] mt-0.5" data-id="element-323">•</span>
                    Soil preparation after snow melt
                  </li>
                </ul>
              </div>
            </div>

            {/* Summer */}
            <div className="bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] overflow-hidden" data-id="element-324">
              <div className="h-1.5 bg-[#FFD166]" data-id="element-325" />
              <div className="p-6" data-id="element-326">
                <span className="text-4xl block mb-3" data-id="element-327">☀️</span>
                <h4 className="font-sans font-bold text-forest-dark text-sm uppercase tracking-wider mb-4" data-id="element-328">
                  Summer Protocol
                </h4>
                <ul className="space-y-2.5" data-id="element-329">
                  <li className="flex items-start gap-2 text-sm text-gray-600 font-sans" data-id="element-330">
                    <span className="text-[#FFD166] mt-0.5" data-id="element-331">•</span>
                    Deep hydration timing for 90°F+ days
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600 font-sans" data-id="element-332">
                    <span className="text-[#FFD166] mt-0.5" data-id="element-333">•</span>
                    Shade strategies using the Cloud Cover Hack
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600 font-sans" data-id="element-334">
                    <span className="text-[#FFD166] mt-0.5" data-id="element-335">•</span>
                    Signs of heat stress before it's too late
                  </li>
                </ul>
              </div>
            </div>

            {/* Fall */}
            <div className="bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] overflow-hidden" data-id="element-336">
              <div className="h-1.5 bg-burnt" data-id="element-337" />
              <div className="p-6" data-id="element-338">
                <span className="text-4xl block mb-3" data-id="element-339">🍂</span>
                <h4 className="font-sans font-bold text-forest-dark text-sm uppercase tracking-wider mb-4" data-id="element-340">
                  Fall Protocol
                </h4>
                <ul className="space-y-2.5" data-id="element-341">
                  <li className="flex items-start gap-2 text-sm text-gray-600 font-sans" data-id="element-342">
                    <span className="text-burnt mt-0.5" data-id="element-343">•</span>
                    Pre-frost checklist (The 10 PM Action Plan)
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600 font-sans" data-id="element-344">
                    <span className="text-burnt mt-0.5" data-id="element-345">•</span>
                    Bulb storage and root protection
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600 font-sans" data-id="element-346">
                    <span className="text-burnt mt-0.5" data-id="element-347">•</span>
                    Storm-proofing before winter winds arrive
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* BLOCK 5 — WITHOUT VS WITH COMPARISON        */}
      {/* ═══════════════════════════════════════════ */}
      <section className="bg-white py-16 md:py-24 px-5" data-id="element-348">
        <div className="max-w-[1100px] mx-auto" data-id="element-349">
          <h3 className="font-serif text-forest text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-12" data-id="element-350">
            Two Gardeners. Same Storm. Very Different Results.
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-0 max-w-4xl mx-auto relative" data-id="element-351">
            {/* VS Badge */}
            <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20" data-id="element-352">
              <div className="w-14 h-14 rounded-full bg-forest shadow-xl flex items-center justify-center border-4 border-white" data-id="element-353">
                <span className="font-sans font-bold text-white text-sm" data-id="element-354">
                  VS
                </span>
              </div>
            </div>

            {/* WITHOUT */}
            <div className="rounded-xl md:rounded-r-none overflow-hidden border border-red-200" data-id="element-355">
              <div className="bg-red-50 px-6 py-4" data-id="element-356">
                <h4 className="font-sans font-bold text-red-600 text-lg" data-id="element-357">
                  ❌ Without The System
                </h4>
              </div>
              <div className="px-6 py-5 space-y-3.5 bg-white" data-id="element-358">
                <div className="flex items-start gap-2.5" data-id="element-359">
                  <span className="text-red-400 font-bold mt-0.5 flex-shrink-0" data-id="element-360">
                    ✗
                  </span>
                  <span className="font-sans text-gray-600 text-[15px]" data-id="element-361">
                    Checks the weather after damage is done
                  </span>
                </div>
                <div className="flex items-start gap-2.5" data-id="element-362">
                  <span className="text-red-400 font-bold mt-0.5 flex-shrink-0" data-id="element-363">
                    ✗
                  </span>
                  <span className="font-sans text-gray-600 text-[15px]" data-id="element-364">
                    Follows random internet tips not built for their zone
                  </span>
                </div>
                <div className="flex items-start gap-2.5" data-id="element-365">
                  <span className="text-red-400 font-bold mt-0.5 flex-shrink-0" data-id="element-366">
                    ✗
                  </span>
                  <span className="font-sans text-gray-600 text-[15px]" data-id="element-367">
                    Spends $200–$500 replacing dead plants every year
                  </span>
                </div>
                <div className="flex items-start gap-2.5" data-id="element-368">
                  <span className="text-red-400 font-bold mt-0.5 flex-shrink-0" data-id="element-369">
                    ✗
                  </span>
                  <span className="font-sans text-gray-600 text-[15px]" data-id="element-370">
                    Panics when a freeze warning hits at 9 PM
                  </span>
                </div>
                <div className="flex items-start gap-2.5" data-id="element-371">
                  <span className="text-red-400 font-bold mt-0.5 flex-shrink-0" data-id="element-372">
                    ✗
                  </span>
                  <span className="font-sans text-gray-600 text-[15px]" data-id="element-373">
                    Has a "hope for the best" strategy
                  </span>
                </div>
                <div className="flex items-start gap-2.5" data-id="element-374">
                  <span className="text-red-400 font-bold mt-0.5 flex-shrink-0" data-id="element-375">
                    ✗
                  </span>
                  <span className="font-sans text-gray-600 text-[15px]" data-id="element-376">
                    Repeats the same mistakes every season
                  </span>
                </div>
              </div>
            </div>

            {/* Mobile VS */}
            <div className="flex md:hidden justify-center -my-2" data-id="element-377">
              <div className="w-12 h-12 rounded-full bg-forest shadow-lg flex items-center justify-center" data-id="element-378">
                <span className="font-sans font-bold text-white text-sm" data-id="element-379">
                  VS
                </span>
              </div>
            </div>

            {/* WITH */}
            <div className="rounded-xl md:rounded-l-none overflow-hidden border border-green-200" data-id="element-380">
              <div className="bg-[#D8F3DC] px-6 py-4" data-id="element-381">
                <h4 className="font-sans font-bold text-forest text-lg" data-id="element-382">
                  ✅ With The 365 Garden Shield
                </h4>
              </div>
              <div className="px-6 py-5 space-y-3.5 bg-white" data-id="element-383">
                <div className="flex items-start gap-2.5" data-id="element-384">
                  <span className="text-forest font-bold mt-0.5 flex-shrink-0" data-id="element-385">
                    ✓
                  </span>
                  <span className="font-sans text-gray-600 text-[15px]" data-id="element-386">
                    Checks their seasonal protocol before weather hits
                  </span>
                </div>
                <div className="flex items-start gap-2.5" data-id="element-387">
                  <span className="text-forest font-bold mt-0.5 flex-shrink-0" data-id="element-388">
                    ✓
                  </span>
                  <span className="font-sans text-gray-600 text-[15px]" data-id="element-389">
                    Follows zone-specific steps for their exact climate
                  </span>
                </div>
                <div className="flex items-start gap-2.5" data-id="element-390">
                  <span className="text-forest font-bold mt-0.5 flex-shrink-0" data-id="element-391">
                    ✓
                  </span>
                  <span className="font-sans text-gray-600 text-[15px]" data-id="element-392">
                    Saves their entire plant investment year after year
                  </span>
                </div>
                <div className="flex items-start gap-2.5" data-id="element-393">
                  <span className="text-forest font-bold mt-0.5 flex-shrink-0" data-id="element-394">
                    ✓
                  </span>
                  <span className="font-sans text-gray-600 text-[15px]" data-id="element-395">
                    Has a 10 PM Frost Checklist ready to execute
                  </span>
                </div>
                <div className="flex items-start gap-2.5" data-id="element-396">
                  <span className="text-forest font-bold mt-0.5 flex-shrink-0" data-id="element-397">
                    ✓
                  </span>
                  <span className="font-sans text-gray-600 text-[15px]" data-id="element-398">
                    Has a clear, confident plan for every season
                  </span>
                </div>
                <div className="flex items-start gap-2.5" data-id="element-399">
                  <span className="text-forest font-bold mt-0.5 flex-shrink-0" data-id="element-400">
                    ✓
                  </span>
                  <span className="font-sans text-gray-600 text-[15px]" data-id="element-401">
                    Breaks the "plant and replace" cycle for good
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* CTA below comparison */}
          <div className="text-center mt-12" data-id="element-402">
            <a href={getHotmartUrl()} target="_blank" rel="noopener noreferrer" onClick={trackAddToCart} className="cta-heartbeat inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-lg font-bold py-4 px-10 rounded-lg shadow-lg transition-colors duration-200 mb-3" data-id="element-403">
              <span data-id="element-404">🛡️</span>
              <span data-id="element-405">Get The Complete System — Only $6.37</span>
            </a>
            <p className="text-sm text-gray-500 font-sans" data-id="element-406">
              Instant PDF Download · Works on any device · 7-Day Money Back
              Guarantee
            </p>
          </div>
        </div>
      </section>
    </div>;
}